import { Component, DestroyRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { DjiFlightAdminService } from '../../core/api-services/dji-flight-admin.service';
import { ToastService } from '../../core/services/toast.service';
import { ConfirmationModalComponent } from '../../shared/components/confirmation-modal.component';
import { ConfirmationModalWithReasonComponent } from '../../shared/components/confirmation-modal-with-reason.component';
import { DjiDroneCandidate } from '../../core/response-models/dji-drone-candidate.model';
import { DjiDroneRef } from '../../core/response-models/dji-drone-ref.model';
import { DjiFlightDetail } from '../../core/data-models/dji-flight-detail.model';
import { DjiLinkStatus } from '../../core/data-models/dji-link-status.model';
import { DjiLinkView } from '../../core/response-models/dji-link-view.model';
import { OrganizationOption } from '../../core/response-models/organization-option.model';
import {
  actionLabel,
  availableActions,
  detailStatusLabel,
  formatApplied,
  formatDuration,
  formatInstant,
  formatSpeed,
  linkRouterTarget,
  relinkMessage,
  statusBadgeClass,
  statusLabel,
} from '../../core/utils/dji-flight.utils';

type Confirming = 'dismiss' | 'invalid' | 'restore' | 'unlinkDrone' | 'unlinkRecord' | 'relink' | 'setDrone' | null;

/**
 * One DJI flight (ADN-2451): what it is, why it is unresolved, how it is linked
 * and what has been done to it, with the actions that resolve it. The Hub
 * decides what is allowed (ADN-2450); this page only offers the actions it will
 * accept, asks before each one, and shows the Hub's own message when one fails.
 */
@Component({
  selector: 'app-dji-flight-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, ConfirmationModalComponent, ConfirmationModalWithReasonComponent],
  templateUrl: './dji-flight-detail.component.html',
  styleUrls: ['./dji-flight-detail.component.scss'],
})
export class DjiFlightDetailComponent implements OnInit {
  intakeId = '';
  flight: DjiFlightDetail | null = null;
  loading = true;
  loadError = '';

  /** The result of the last action, shown on the page until the next one. */
  result: { type: 'success' | 'info'; text: string } | null = null;
  actionError = '';
  processing = false;
  confirming: Confirming = null;

  // Choosing a drone and organization
  resolving = false;
  organizations: OrganizationOption[] = [];
  selectedDrone: DjiDroneRef | null = null;
  selectedOrganizationId = '';
  droneSearch = '';
  droneResults: DjiDroneRef[] = [];
  searchingDrones = false;
  private readonly droneSearchInput = new Subject<string>();

  constructor(
    private route: ActivatedRoute,
    private service: DjiFlightAdminService,
    private toast: ToastService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.intakeId = this.route.snapshot.paramMap.get('id') ?? '';
    this.load();
    this.droneSearchInput.pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe((text) => this.searchDrones(text));
  }

  get actions() {
    return this.flight ? availableActions(this.flight) : null;
  }

  get isSetAside(): boolean {
    return this.flight?.linkStatus === 'dismissed' || this.flight?.linkStatus === 'invalid';
  }

  get selectedOrganizationName(): string {
    return this.organizations.find((o) => o.id === this.selectedOrganizationId)?.name ?? '';
  }

  get canSubmitResolve(): boolean {
    return !!this.selectedDrone && !!this.selectedOrganizationId && !this.processing;
  }

  /** What confirming "set the drone" will do, in the words the admin reads on the confirmation. */
  get setDroneMessage(): string {
    if (!this.selectedDrone) return '';
    return `Link this flight to ${this.selectedDrone.serialNumber} and assign it to ${this.selectedOrganizationName}? This replaces any drone it is linked to now, and automatic matching will not change it again.`;
  }

  // ---- choosing a drone and organization ---------------------------------------

  startResolving(): void {
    this.resolving = true;
    this.actionError = '';
    if (this.organizations.length === 0) {
      this.service
        .listOrganizations()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({ next: (orgs) => (this.organizations = orgs), error: (err: Error) => (this.actionError = err.message) });
    }
    // An exact match is the obvious choice when there is only one.
    const only = this.flight?.droneCandidates.length === 1 ? this.flight.droneCandidates[0] : null;
    if (only) this.pickCandidate(only);
    else this.selectedOrganizationId = this.flight?.organizationId ?? '';
  }

  cancelResolving(): void {
    this.resolving = false;
    this.selectedDrone = null;
    this.selectedOrganizationId = '';
    this.droneSearch = '';
    this.droneResults = [];
  }

  /** An exact-serial drone: the organization defaults to who owned it at the time of the flight. */
  pickCandidate(candidate: DjiDroneCandidate): void {
    this.selectedDrone = candidate;
    this.selectedOrganizationId = candidate.ownerAtFlight.organizationId ?? candidate.organizationId ?? this.flight?.organizationId ?? '';
  }

  /** Any drone found by search: an organization drone defaults to its owner. */
  pickSearched(drone: DjiDroneRef): void {
    this.selectedDrone = drone;
    this.selectedOrganizationId = drone.organizationId ?? this.selectedOrganizationId ?? this.flight?.organizationId ?? '';
    this.droneSearch = '';
    this.droneResults = [];
  }

  onDroneSearchInput(text: string): void {
    this.droneSearchInput.next(text.trim());
  }

  // ---- actions -------------------------------------------------------------------

  confirm(what: Confirming): void {
    this.actionError = '';
    this.confirming = what;
  }

  cancelConfirm(): void {
    if (!this.processing) this.confirming = null;
  }

  onSetDroneConfirmed(): void {
    if (!this.selectedDrone || !this.selectedOrganizationId) return;
    const drone = this.selectedDrone;
    this.run(
      this.service.setDroneAndOrganization(this.intakeId, {
        droneId: drone.id,
        droneSource: drone.source,
        organizationId: this.selectedOrganizationId,
      }),
      (status) => `Drone and organization set. The flight is now ${statusLabel(status).toLowerCase()}.`,
      () => this.cancelResolving(),
    );
  }

  onDismissConfirmed(note: string): void {
    this.run(this.service.dismiss(this.intakeId, note || undefined), () => 'Flight dismissed.');
  }

  onInvalidConfirmed(note: string): void {
    this.run(this.service.markInvalid(this.intakeId, note || undefined), () => 'Flight marked invalid.');
  }

  onRestoreConfirmed(): void {
    this.run(this.service.restore(this.intakeId), (status) => `Flight restored. It is now ${statusLabel(status).toLowerCase()}.`);
  }

  onUnlinkConfirmed(target: 'drone' | 'spray_record'): void {
    this.run(this.service.unlink(this.intakeId, target), () => (target === 'drone' ? 'Drone unlinked.' : 'Spray record unlinked.'));
  }

  onRelinkConfirmed(): void {
    this.processing = true;
    this.service
      .relink(this.intakeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ outcome }) => {
          const text = relinkMessage(outcome);
          this.result = { type: outcome.sprayRecord === 'linked' ? 'success' : 'info', text };
          this.toast.info(text);
          this.finishAction(true);
        },
        error: (err: Error) => this.failAction(err),
      });
  }

  // ---- display helpers ---------------------------------------------------------

  statusLabel = statusLabel;
  statusBadgeClass = statusBadgeClass;
  detailStatusLabel = detailStatusLabel;
  actionLabel = actionLabel;
  formatInstant = formatInstant;
  formatDuration = formatDuration;
  formatSpeed = formatSpeed;
  formatApplied = formatApplied;

  /** The admin page for a link's target, when the portal has one (see `linkRouterTarget`). */
  linkTarget(link: DjiLinkView): { commands: unknown[]; queryParams?: Record<string, string> } | null {
    return this.flight ? linkRouterTarget(link, this.flight) : null;
  }

  linkTypeLabel(type: string): string {
    return { adn_drone: 'Drone (ADN inventory)', organization_drone: 'Drone (organization-owned)', spray_record: 'Spray record', job: 'Job' }[type] ?? type;
  }

  detailNote(details: Record<string, unknown> | null): string | null {
    const note = details?.['note'];
    return typeof note === 'string' && note ? note : null;
  }

  // ---- internals ---------------------------------------------------------------

  private load(quiet = false): void {
    if (!quiet) this.loading = true;
    this.loadError = '';
    this.service
      .getFlight(this.intakeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (flight) => {
          this.flight = flight;
          this.loading = false;
        },
        error: (err: Error) => {
          this.loadError = err.message;
          this.loading = false;
        },
      });
  }

  private run(call: Observable<DjiLinkStatus>, message: (status: DjiLinkStatus) => string, then?: () => void): void {
    this.processing = true;
    call.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (status) => {
        const text = message(status);
        this.result = { type: 'success', text };
        this.toast.success(text);
        then?.();
        this.finishAction(true);
      },
      error: (err: Error) => this.failAction(err),
    });
  }

  private finishAction(reload: boolean): void {
    this.processing = false;
    this.confirming = null;
    if (reload) this.load(true);
  }

  /** The Hub refused: show its message, leave the page as it was, and refresh quietly in case someone else already changed the flight. */
  private failAction(err: Error): void {
    this.actionError = err.message;
    this.result = null;
    this.toast.error(err.message);
    this.finishAction(true);
  }

  private searchDrones(text: string): void {
    if (text.length < 2) {
      this.droneResults = [];
      return;
    }
    this.searchingDrones = true;
    this.service
      .searchDrones(text)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (drones) => {
          this.droneResults = drones;
          this.searchingDrones = false;
        },
        error: () => {
          this.droneResults = [];
          this.searchingDrones = false;
        },
      });
  }
}
